import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  fetchDeviceLogs,
  clear,
  changeOrder,
  changePage
} from '#action/device/log';
import { updateBreadcrumb } from '#action/breadcrumb';
import PageBar from '#component/general/page-bar';
import FilterWrapper from '#component/general/list-filter/filter-wrapper-log';
import LogPagination from '#component/general/log-pagination';
import TableView from './table-view';
import MobileView from './mobile-view';
import { getSearchParams } from './helper';
import filterConfig from './filter-components/config';
import { setDocumentTitle } from '#helper';

const DeviceLog = props => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { history } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const account = useSelector(s => s.account, shallowEqual);
  const {
    logs,
    page: initialPage,
    pages,
    order,
    status,
    error
  } = useSelector(s => s.deviceLogs, shallowEqual);
  const [init, setInit] = useState(false);
  const listFilterStatus = useSelector(s => s.listFilter.status, shallowEqual);
  const loading = status !== 'succeeded' || status === 'idle' || !init || !listFilterStatus;
  const filterData = useMemo(() => filterConfig, []);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchDeviceLogs());
      setInit(true);
    };

    if (listFilterStatus) {
      fetchData();
    }

    return () => {
      dispatch(clear());
    };
  }, [dispatch, listFilterStatus]);

  useEffect(() => {
    setDocumentTitle('Devices audit trail');
    dispatch(
      updateBreadcrumb([
        { label: 'Devices', path: '/device' },
        { label: 'Audit trail', path: '/device-log' }
      ])
    );
  }, [dispatch]);

  const replaceHistoryParams = useCallback(newParams => {
    const {
      location: { search }
    } = history;
    const params = getSearchParams(search, newParams);
    history.replace(`?${params}`);
  }, [history]);

  const handleRequestSort = useCallback(() => {
    const isAsc = order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    replaceHistoryParams({ filterType: 'order', value: newOrder });
    dispatch(changeOrder(newOrder));
  }, [order, dispatch, replaceHistoryParams]);

  const viewProps = useMemo(
    () => ({
      logs,
      order,
      account,
      loading,
      error,
      handleRequestSort
    }),
    [logs, order, account, loading, error, handleRequestSort]
  );

  return (
    <section>
      <PageBar view="log">
        <FilterWrapper data={filterData} />
      </PageBar>
      {isMobile ? <MobileView {...viewProps} /> : <TableView {...viewProps} />}
      <LogPagination
        history={history}
        initialPage={initialPage}
        pages={pages}
        changePageAction={changePage}
        getSearchParams={getSearchParams}
      />
    </section>
  );
};

export default DeviceLog;
